/**
 * User Model - Centralized schema definition for users
 * This is the single source of truth for user structure
 * Matches the Rails User model schema
 */

/**
 * User schema definition
 * All user objects should conform to this structure
 */
export const UserSchema = {
  // Required fields
  id: {
    type: String,
    required: true,
    description: 'Unique identifier for the user (UUID)'
  },
  email: {
    type: String,
    required: true,
    description: 'User email address'
  },
  firstName: {
    type: String,
    required: true,
    description: 'User first name'
  },
  lastName: {
    type: String,
    required: true,
    description: 'User last name'
  },
  experienceLevel: {
    type: String,
    required: true,
    description: 'Experience level of the dietitian'
  },
  city: {
    type: String,
    required: true,
    description: 'City where the user is located'
  },
  state: {
    type: String,
    required: true,
    description: 'State where the user is located'
  },

  // Optional fields
  professionalRole: {
    type: String,
    required: false,
    description: 'Professional role: RDN (Registered Dietitian Nutritionist) or NDTR (Nutrition and Dietetics Technician, Registered)'
  },
  isAdmin: {
    type: Boolean,
    required: false,
    description: 'Whether the user is an admin'
  },
  licenseNumber: {
    type: String,
    required: false,
    description: 'Dietitian license number'
  },
  academyNumber: {
    type: String,
    required: false,
    description: 'Academy of Nutrition and Dietetics number'
  },

  // // Authentication fields (from Devise)
  // provider: {
  //   type: String,
  //   required: false,
  //   description: 'OAuth provider (e.g., google)'
  // },
  // uid: {
  //   type: String,
  //   required: false,
  //   description: 'User ID from OAuth provider'
  // },

  // System fields
  createdAt: {
    type: String,
    required: false,
    description: 'ISO 8601 timestamp of when the user was created'
  },
  updatedAt: {
    type: String,
    required: false,
    description: 'ISO 8601 timestamp of when the user was last updated'
  }
}

/**
 * Create a new user object with default values
 * @param {Object} data - User data
 * @returns {Object} User object
 */
export function createUser(data = {}) {
  const now = new Date().toISOString()
  
  return {
    id: data.id || null,
    email: data.email || '',
    firstName: data.firstName || data.first_name || '',
    lastName: data.lastName || data.last_name || '',
    experienceLevel: data.experienceLevel || data.experience_level || '',
    city: data.city || '',
    state: data.state || '',
    professionalRole: data.professionalRole || data.professional_role || null,
    licenseNumber: data.licenseNumber || data.license_number || null,
    academyNumber: data.academyNumber || data.academy_number || null,
    isAdmin: data.isAdmin !== undefined ? data.isAdmin : (data.is_admin !== undefined ? data.is_admin : false),
    provider: data.provider || null,
    uid: data.uid || null,
    createdAt: data.createdAt || data.created_at || now,
    updatedAt: data.updatedAt || data.updated_at || now
  }
}

/**
 * Convert user object from Rails API format (snake_case) to frontend format (camelCase)
 * @param {Object} railsUser - User object from Rails API
 * @returns {Object} User object in frontend format
 */
export function fromRailsFormat(railsUser) {
  if (!railsUser) return null
  
  return {
    id: railsUser.id,
    email: railsUser.email,
    firstName: railsUser.first_name,
    lastName: railsUser.last_name,
    experienceLevel: railsUser.experience_level,
    city: railsUser.city,
    state: railsUser.state,
    professionalRole: railsUser.professional_role,
    licenseNumber: railsUser.license_number,
    academyNumber: railsUser.academy_number,
    isAdmin: railsUser.is_admin || false,
    provider: railsUser.provider,
    providerId: railsUser.provider_id,
    uid: railsUser.uid,
    createdAt: railsUser.created_at,
    updatedAt: railsUser.updated_at
  }
}

/**
 * Convert user object from frontend format (camelCase) to Rails API format (snake_case)
 * @param {Object} user - User object in frontend format
 * @returns {Object} User object in Rails API format
 */
export function toRailsFormat(user) {
  if (!user) return null
  
  return {
    id: user.id,
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
    experience_level: user.experienceLevel,
    city: user.city,
    state: user.state,
    professional_role: user.professionalRole,
    license_number: user.licenseNumber,
    academy_number: user.academyNumber,
    is_admin: user.isAdmin,
    provider: user.provider,
    uid: user.uid
  }
}

/**
 * Validate a user object against the schema
 * @param {Object} user - User object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validateUser(user) {
  const errors = []

  // Required fields
  if (!user.email || user.email.trim() === '') {
    errors.push('Email is required')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
    errors.push('Email must be a valid email address')
  }
  
  if (!user.firstName || user.firstName.trim() === '') {
    errors.push('First name is required')
  }
  
  if (!user.lastName || user.lastName.trim() === '') {
    errors.push('Last name is required')
  }
  
  if (!user.experienceLevel || user.experienceLevel.trim() === '') {
    errors.push('Experience level is required')
  }
  
  if (!user.city || user.city.trim() === '') {
    errors.push('City is required')
  }
  
  if (!user.state || user.state.trim() === '') {
    errors.push('State is required')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

